import {
  createContext,
  useState,
  ReactNode,
  useContext
} from "react";
import api, { apiV2 } from "../services/api";

interface FinancialData {
  issalaryTrue: Array<{
    type: string;
    employee: string;
    total_value_exec: number;
    total_value_pend: number;
    total_global: number;
  }>;
  issalaryNull: Array<{
    type: string;
    provider: string;
    total_value_exec: number;
    total_value_pend: number;
    total_global: number;
  }>;
  summary: {
    entradas: {
      total_exec: number;
      total_pend: number;
      total_global: number;
    };
    saidas: {
      total_exec: number;
      total_pend: number;
      total_global: number;
    };
  };
}

interface FinancialProviderProps {
  children: ReactNode;
}

interface TripBoatPair {
  trip_name?: string;
  boat_name?: string;
}

interface OverviewRequest {
  trip_name?: string | string[];
  boats_name?: string | string[];
  harvest: string;
  fishingtypes_name?: string | string[];
  product_name?: string | string[];
  trip_boat_pairs: TripBoatPair[];
  pages: number;
  limit: number;
}

interface ResponsePropsWithData {
  success: boolean;
  message: string;
  data: any;
}

interface FinancialContextData {
  financialData: FinancialData | null;
  loading: boolean;
  getCcOverview: (request: OverviewRequest) => Promise<ResponsePropsWithData>;
  getCcOverviewFinishedProductOrders: (request: OverviewRequest) => Promise<ResponsePropsWithData>;
  getStockValue: (request: OverviewRequest) => Promise<ResponsePropsWithData>;
  getCurrentStockValue: () => Promise<ResponsePropsWithData>;
  getStockValueFinishedProductOrders: (request: OverviewRequest) => Promise<ResponsePropsWithData>;
  getCashFlowData: (request: OverviewRequest) => Promise<ResponsePropsWithData>;
  getOpenInstallmentsValueInHarvest: (harvest: number) => Promise<ResponsePropsWithData>;
  getResultData: (request: OverviewRequest) => Promise<ResponsePropsWithData>;
  getFinancialPanelData: () => Promise<ResponsePropsWithData>;
  getEconomicAnalysisBoatsData: (harvest: number) => Promise<ResponsePropsWithData>;
  getFinancialAnalysisBoatsData: (harvest: number) => Promise<ResponsePropsWithData>;
}

const FinancialContext = createContext<FinancialContextData>(
  {} as FinancialContextData
);

function isTechnicalMessage(message: unknown) {
  if (typeof message !== "string") return true;

  return /^(cannot\s+(get|post|put|patch|delete)|axioserror|error:|network error|internal server error|failed with status code)/i.test(message.trim()) ||
    /<\/?[a-z][\s\S]*>/i.test(message) ||
    /\/api\/v\d/i.test(message);
}

function translateKnownFinancialApiMessage(
  message: unknown,
  validationMessage = "Não foi possível validar os dados enviados. Revise os filtros e tente novamente."
) {
  if (typeof message !== "string") return undefined;

  const normalizedMessage = message.trim();

  if (/^validation failed\.?$/i.test(normalizedMessage) ||
    /(harvest).*(number|integer|min|max|greater than|less than)/i.test(normalizedMessage)) {
    return validationMessage;
  }

  if (/^you do not have permission to perform this action\.?$/i.test(normalizedMessage)) {
    return "Você não tem permissão para realizar esta ação.";
  }

  if (/^unauthorized\.?$/i.test(normalizedMessage)) {
    return "Sessão expirada ou não autorizada. Faça login novamente.";
  }

  return undefined;
}

function getApiMessage(response: any) {
  return response?.data?.message || response?.message || response?.data?.errors?.message || response?.data?.errors?.toString?.();
}

function isSuccessfulResponse(response: any) {
  return response?.success !== false && response?.status >= 200 && response?.status < 300;
}

function getFinancialApiErrorMessage(response: any, fallbackMessage: string, validationMessage?: string) {
  const apiMessage = getApiMessage(response);
  const translatedMessage = translateKnownFinancialApiMessage(apiMessage, validationMessage);

  return translatedMessage || (isTechnicalMessage(apiMessage) ? fallbackMessage : apiMessage);
}

export function FinancialProvider({ children }: FinancialProviderProps) {
  const [financialData] = useState<FinancialData | null>(
    null
  );

  const [loading, setLoading] = useState<boolean>(true);

  async function getCcOverview(
    request: OverviewRequest
  ): Promise<ResponsePropsWithData> {
    setLoading(true);
    try {
      let params = {
        ...request
      };
      const response = await api.get("/ccOverview", {
        params: { ...api.defaults.params, ...params }
      });
      console.log(response);
      if (response.status === 200) {
        // setFinancialData(response.data);
        return {
          success: true,
          message: "",
          data: response.data
        };
      }
      return {
        success: false,
        message: "Erro ao consultar os dados financeiros.",
        data: []
      };
    } catch (error) {
      console.error("Erro ao buscar dados financeiros:", error);
      return {
        success: false,
        message: "Erro ao consultar os dados financeiros.",
        data: []
      };
    } finally {
      setLoading(false);
    }
  }

  async function getCcOverviewFinishedProductOrders(
    request: OverviewRequest
  ): Promise<ResponsePropsWithData> {
    setLoading(true);

    try {
      let params = {
        ...request
      };

      const response = await api.get("/ccOverview-finished-product-orders", {
        params: { ...api.defaults.params, ...params }
      });

      if (response.status === 200) {
        return {
          success: true,
          message: "",
          data: response.data
        };
      }

      return {
        success: false,
        message: "Erro ao consultar os dados financeiros dos pedidos de produto acabado.",
        data: []
      };
    } catch (error) {
      console.error("Erro ao buscar dados financeiros:", error);
      return {
        success: false,
        message: "Erro ao consultar os dados financeiros.",
        data: []
      };
    } finally {
      setLoading(false);
    }
  }

  async function getStockValue(request: OverviewRequest): Promise<ResponsePropsWithData> {
    setLoading(true);

    try {
      let params = {
        ...request
      };

      const response = await apiV2.get("/harvest-stock-value", {
        params
      });

      if (isSuccessfulResponse(response)) {
        return {
          success: true,
          message: "",
          data: response.data
        };
      }

      return {
        success: false,
        message: getFinancialApiErrorMessage(response, "Erro ao consultar o valor do estoque de insumos.", "Informe uma safra válida."),
        data: []
      };
    } catch (error) {
      console.error("Erro ao buscar o valor do estoque de insumos:", error);
      return {
        success: false,
        message: "Erro ao consultar o valor do estoque de insumos.",
        data: []
      };
    } finally {
      setLoading(false);
    }
  }

  async function getCurrentStockValue(): Promise<ResponsePropsWithData> {
    setLoading(true);

    try {
      const response = await apiV2.get("/stock-value");

      if (isSuccessfulResponse(response)) {
        return {
          success: true,
          message: "",
          data: response.data
        };
      }

      return {
        success: false,
        message: getFinancialApiErrorMessage(response, "Erro ao consultar o valor atual do estoque de insumos."),
        data: []
      };
    } catch (error) {
      console.error("Erro ao buscar o valor atual do estoque de insumos:", error);
      return {
        success: false,
        message: "Erro ao consultar o valor atual do estoque de insumos.",
        data: []
      };
    } finally {
      setLoading(false);
    }
  }

  async function getStockValueFinishedProductOrders(request: OverviewRequest): Promise<ResponsePropsWithData> {
    setLoading(true);

    try {
      let params = {
        ...request
      };

      const response = await api.get("/harvest-stock-value-finished-product-orders", {
        params: { ...api.defaults.params, ...params }
      });

      if (response.status === 200) {
        return {
          success: true,
          message: "",
          data: response.data
        };
      }

      return {
        success: false,
        message: "Erro ao consultar o valor do estoque de pedidos de produto acabado.",
        data: []
      };
    } catch (error) {
      console.error("Erro ao buscar o valor do estoque de pedidos de produto acabado:", error);
      return {
        success: false,
        message: "Erro ao consultar o valor do estoque de pedidos de produto acabado.",
        data: []
      };
    } finally {
      setLoading(false);
    }
  }

  async function getCashFlowData(request: OverviewRequest): Promise<ResponsePropsWithData> {
    setLoading(true);

    try {
      let params = {
        ...request
      };

      const response = await api.get("/cash-flow", {
        params: { ...api.defaults.params, ...params }
      });

      if (response.status === 200) {
        return {
          success: true,
          message: "",
          data: response.data
        };
      }

      return {
        success: false,
        message: "Erro ao consultar os dados de fluxo de caixa.",
        data: []
      };
    } catch (error) {
      console.error("Erro ao buscar os dados de fluxo de caixa:", error);
      return {
        success: false,
        message: "Erro ao consultar os dados de fluxo de caixa.",
        data: []
      };
    } finally {
      setLoading(false);
    }
  }

  async function getResultData(request: OverviewRequest): Promise<ResponsePropsWithData> {
    setLoading(true);

    try {
      let params = {
        ...request
      };

      const response = await api.get("/result", {
        params: { ...api.defaults.params, ...params }
      });

      if (response.status === 200) {
        return {
          success: true,
          message: "",
          data: response.data
        };
      }

      return {
        success: false,
        message: "Erro ao consultar os dados de resultado mensal na safra.",
        data: []
      };
    } catch (error) {
      console.error("Erro ao buscar os dados de resultado mensal na safra:", error);
      return {
        success: false,
        message: "Erro ao consultar os dados de resultado mensal na safra.",
        data: []
      };
    } finally {
      setLoading(false);
    }
  }

  async function getOpenInstallmentsValueInHarvest(harvest: number): Promise<ResponsePropsWithData> {
    setLoading(true);

    try {
      let params = {
        harvest
      };

      const response = await api.get("/open-installments-value", {
        params: { ...api.defaults.params, ...params }
      });

      if (response.status === 200) {
        return {
          success: true,
          message: "",
          data: response.data
        };
      }

      return {
        success: false,
        message: "Erro ao consultar o valor dos parcelamentos abertos na safra.",
        data: []
      };
    } catch (error) {
      console.error("Erro ao buscar o valor dos parcelamentos abertos na safra:", error);
      return {
        success: false,
        message: "Erro ao consultar o valor dos parcelamentos abertos na safra.",
        data: []
      };
    } finally {
      setLoading(false);
    }
  }

  async function getFinancialPanelData(): Promise<ResponsePropsWithData> {
    setLoading(true);

    try {
      const response = await api.get("/financial-dashboard");

      if (response.status === 200) {
        return {
          success: true,
          message: "",
          data: response.data
        };
      }

      return {
        success: false,
        message: "Erro ao consultar os dados do painel financeiro.",
        data: []
      };
    } catch (error) {
      console.error("Erro ao buscar os dados do painel financeiro:", error);
      return {
        success: false,
        message: "Erro ao consultar os dados do painel financeiro.",
        data: []
      };
    } finally {
      setLoading(false);
    }
  }

  async function getEconomicAnalysisBoatsData(harvest: number): Promise<ResponsePropsWithData> {
    setLoading(true);

    try {
      let params = {
        harvest
      };

      const response = await api.get("/boat-company-financial-summary", {
        params: { ...api.defaults.params, ...params }
      });

      if (response.status === 200) {
        return {
          success: true,
          message: "",
          data: response.data
        };
      }

      return {
        success: false,
        message: "Erro ao consultar os dados da análise econômica dos barcos.",
        data: []
      };
    } catch (error) {
      console.error("Erro ao buscar os dados da análise econômica dos barcos:", error);
      return {
        success: false,
        message: "Erro ao consultar os dados da análise econômica dos barcos.",
        data: []
      };
    } finally {
      setLoading(false);
    }
  }

  async function getFinancialAnalysisBoatsData(harvest: number): Promise<ResponsePropsWithData> {
    setLoading(true);

    try {
      let params = {
        harvest
      };

      const response = await api.get("/boat-company-operational-cash-flow", {
        params: { ...api.defaults.params, ...params }
      });

      if (response.status === 200) {
        return {
          success: true,
          message: "",
          data: response.data
        };
      }

      return {
        success: false,
        message: "Erro ao consultar os dados da análise financeira dos barcos.",
        data: []
      };
    } catch (error) {
      console.error("Erro ao buscar os dados da análise financeira dos barcos:", error);
      return {
        success: false,
        message: "Erro ao consultar os dados da análise financeira dos barcos.",
        data: []
      };
    } finally {
      setLoading(false);
    }
  }

  return (
    <FinancialContext.Provider
      value={{
        financialData,
        loading,
        getCcOverview,
        getCcOverviewFinishedProductOrders,
        getStockValue,
        getCurrentStockValue,
        getStockValueFinishedProductOrders,
        getCashFlowData,
        getOpenInstallmentsValueInHarvest,
        getResultData,
        getFinancialPanelData,
        getEconomicAnalysisBoatsData,
        getFinancialAnalysisBoatsData,
      }}
    >
      {children}
    </FinancialContext.Provider>
  );
}

export function useFinancialData() {
  const context = useContext(FinancialContext);
  return context;
}
